/**
 * OrgUnitFormModal 컴포넌트
 * V145: 조직장 개념 제거
 * V145.1: 승인자 기능 복구 (향후 근로/휴가 승인 워크플로우용)
 * V26.1: type='company'일 때 companyCode 필수 입력 추가
 *
 * **역할:**
 * - 조직 생성/수정 폼을 모달 형태로 제공
 * - 생성 모드: 새 조직 추가
 * - 수정 모드: 기존 조직 정보 수정
 *
 * **사용 예시:**
 * ```tsx
 * const [isModalOpen, setIsModalOpen] = useState(false);
 *
 * <OrgUnitFormModal
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   onSuccess={() => {
 *     setIsModalOpen(false);
 *     refetchOrgUnits(); // 목록 새로고침
 *   }}
 *   orgUnitId={editingOrgUnitId} // 수정 모드
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { X, Save, Loader, UserPlus, UserMinus } from 'lucide-react';
import { useOrgUnit } from '../../../shared/hooks/useOrgUnits';
import { useOrgUnits } from '../../../shared/hooks/useOrgUnits';
// V154: doc, getDoc 제거 - Cloud Function 경유로 변경
import { useOrgUnitMutations } from '../../../shared/hooks/useOrgUnitMutations';
import {
  OrgUnitType,
  CreateOrgUnitInput,
  UpdateOrgUnitInput,
} from '../../../shared/types/orgUnit';
// V145.1: 승인자 선택을 위해 EmployeePickerModal 복구
import { EmployeePickerModal } from './EmployeePickerModal';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';

/**
 * OrgUnitFormModal Props
 */
export interface OrgUnitFormModalProps {
  /**
   * 모달 열림 여부
   */
  isOpen: boolean;

  /**
   * 모달 닫기 콜백
   */
  onClose: () => void;

  /**
   * 생성/수정 성공 시 콜백
   */
  onSuccess: () => void;

  /**
   * 수정할 조직 ID (생성 모드면 undefined)
   */
  orgUnitId?: string;
}

/**
 * 조직 타입 옵션
 */
const ORG_TYPE_OPTIONS: Array<{ value: OrgUnitType; label: string }> = [
  { value: 'company', label: '회사' },
  { value: 'headquarter', label: '본부' },
  { value: 'division', label: '실' },
  { value: 'team', label: '팀' },
  { value: 'other', label: '기타' },
];

/**
 * OrgUnitFormModal 컴포넌트
 */
export function OrgUnitFormModal({
  isOpen,
  onClose,
  onSuccess,
  orgUnitId,
}: OrgUnitFormModalProps) {
  // ============================================
  // 상태 및 Hook
  // ============================================

  const isEditMode = !!orgUnitId;

  // 기존 조직 정보 조회 (수정 모드인 경우)
  const { orgUnit, isLoading: isLoadingOrgUnit } = useOrgUnit(orgUnitId);

  // 상위 조직 선택을 위한 전체 조직 목록
  // ✅ active 상태만 필터링 (삭제되거나 비활성화된 조직 제외)
  const { orgUnits: allOrgUnits } = useOrgUnits();

  // ✅ active 상태 조직만 드롭다운에 표시
  const activeOrgUnits = React.useMemo(() => {
    return allOrgUnits.filter((org) => org.status === 'active');
  }, [allOrgUnits]);

  // Mutations
  const { createOrgUnit, updateOrgUnit, isSubmitting, error } = useOrgUnitMutations();
  const overlayProps = useModalOverlayClose(onClose);

  // ============================================
  // 폼 초기값 (Create 모드 기본값)
  // V145: 조직장 필드 제거 / V145.1: 승인자 필드 복구
  // V26.1: companyCode 필드 추가 (type='company'일 때만 사용)
  // ============================================
  const DEFAULT_FORM_DATA = {
    name: '',
    code: '',
    type: 'team' as OrgUnitType,
    parentId: '',
    order: '',
    isExternal: false,
    externalCompanyName: '',
    description: '',
    // V145.1: 승인자 필드 복구
    approverUserId: '',
    approverName: '',
    // V26.1: 회사 코드 (type='company'일 때만 사용)
    companyCode: '' as '' | 'KR' | 'CN',
  };

  // V26.1: 회사 코드 옵션
  const COMPANY_CODE_OPTIONS: Array<{ value: 'KR' | 'CN'; label: string }> = [
    { value: 'KR', label: 'KR - 한국 법인' },
    { value: 'CN', label: 'CN - 중국 법인' },
  ];

  // 폼 상태
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  // V145.1: 승인자 선택 모달 상태
  const [isApproverPickerOpen, setIsApproverPickerOpen] = useState(false);

  // V75.2: 조직 DL 이메일 목록 상태
  const [dlEmails, setDlEmails] = useState<string[]>([]);

  // ============================================
  // 조직 코드 중복 체크 (프론트엔드 사전 검증) ✅
  // ============================================
  const [codeError, setCodeError] = useState<string>('');

  // 조직 코드 중복 검사
  const checkCodeDuplication = React.useCallback(
    (code: string) => {
      if (!code || code.trim() === '') {
        setCodeError('');
        return false;
      }

      const trimmedCode = code.trim();

      // 수정 모드일 때는 자기 자신 제외
      const isDuplicated = allOrgUnits.some(
        (org) =>
          org.code === trimmedCode &&
          org.id !== orgUnitId // 수정 모드에서 자기 자신은 제외
      );

      if (isDuplicated) {
        setCodeError('⚠️ 이미 사용 중인 조직 코드입니다. 다른 코드를 입력해 주세요.');
        return true;
      }

      setCodeError('');
      return false;
    },
    [allOrgUnits, orgUnitId]
  );

  // V154: 직원 이름 조회 헬퍼 함수 (Cloud Function 경유)
  // 이전: users 컬렉션 직접 read → Firestore Rules로 실패 가능
  // 변경: getEmployee Cloud Function 호출
  const fetchEmployeeName = async (employeeId: string): Promise<string> => {
    if (!employeeId) return '';
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../../firebase/config');
      if (!functions) {
        return '(이름 조회 불가)';
      }
      const getEmployeeFn = httpsCallable<
        { employeeId: string },
        { success: boolean; employee: { nameKo?: string; displayName?: string } }
      >(functions, 'getEmployee');
      const result = await getEmployeeFn({ employeeId });
      if (result.data.success && result.data.employee) {
        return result.data.employee.nameKo || result.data.employee.displayName || '';
      }
    } catch (error) {
      console.error('[V154] 직원 이름 조회 실패:', error);
    }
    return '(이름 조회 실패)';
  };

  // ============================================
  // Effect: 모달 열릴 때 폼 초기화 + 조직 정보 로드
  // ============================================
  // 주의: isOpen과 orgUnitId 변경 시 모두 반응하도록 함
  // 이전에는 닫힐 때 리셋 + 열릴 때 로드가 분리되어 경쟁 조건 발생

  useEffect(() => {
    if (!isOpen) {
      // 모달 닫힘 - 폼 초기화
      setFormData(DEFAULT_FORM_DATA);
      setDlEmails([]);
      setIsApproverPickerOpen(false);
      setCodeError('');
      return;
    }

    // 모달 열림 - 생성 모드면 초기화 상태 유지
    if (!isEditMode) {
      setFormData(DEFAULT_FORM_DATA);
      setDlEmails([]);
      return;
    }

    // 수정 모드 - 로딩 중이면 대기
    if (isLoadingOrgUnit || !orgUnit) {
      return;
    }

    // 수정 모드 - 조직 데이터 로드
    // V145: 조직장 필드 제거 / V145.1: 승인자 필드 복구
    // V26.1: companyCode 필드 추가
    const approverId = orgUnit.approverUserId || orgUnit.approverEmployeeId || '';

    setFormData({
      name: orgUnit.name || '',
      code: orgUnit.code || '',
      type: orgUnit.type,
      parentId: orgUnit.parentId || '',
      order: String(orgUnit.order || ''),
      isExternal: orgUnit.isExternal || false,
      externalCompanyName: orgUnit.externalCompanyName || '',
      description: orgUnit.description || '',
      approverUserId: approverId,
      approverName: '', // 아래에서 비동기 조회
      // V26.1: 회사 코드 로드
      companyCode: (orgUnit.companyCode as '' | 'KR' | 'CN') || '',
    });

    // V75.2: 조직 DL 이메일 로드
    setDlEmails(orgUnit.leaveNotificationDlEmails || []);

    // V145.1: 승인자 이름 비동기 조회
    if (approverId) {
      fetchEmployeeName(approverId).then((name) => {
        setFormData((prev) => ({
          ...prev,
          approverName: name,
        }));
      });
    }
  }, [isOpen, isEditMode, orgUnit, isLoadingOrgUnit]);

  // ============================================
  // 폼 핸들러
  // ============================================

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // ✅ 조직 코드 입력 시 중복 체크
    if (name === 'code') {
      checkCodeDuplication(value);
    }
  };

  // V145.1: 승인자 핸들러 복구
  const handleApproverSelect = (employeeId: string, employeeName: string) => {
    setFormData((prev) => ({
      ...prev,
      approverUserId: employeeId,
      approverName: employeeName,
    }));
    setIsApproverPickerOpen(false);
  };

  const handleApproverClear = () => {
    setFormData((prev) => ({
      ...prev,
      approverUserId: '',
      approverName: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // V26.1: type='company'일 때 companyCode 필수 검증
    if (formData.type === 'company' && !formData.companyCode) {
      alert('회사(company) 유형 조직은 회사 코드(KR/CN)를 필수로 선택해야 합니다.');
      return;
    }

    // V75.5: 조직 DL 이메일 도메인 검증
    const cleanedDlEmails = dlEmails.map(e => e.trim().toLowerCase()).filter(Boolean);
    const invalidDl = cleanedDlEmails.find(e => !e.endsWith('@ngle.co.kr'));
    if (invalidDl) {
      alert(`조직 DL에 @ngle.co.kr 이메일만 입력할 수 있습니다.\n잘못된 주소: ${invalidDl}`);
      return;
    }

    try {
      if (isEditMode && orgUnitId) {
        // 수정 모드
        // V145: 조직장 필드 제거 / V145.1: 승인자 필드 복구
        // V26.1: type='company'일 때 companyCode 포함
        const updateInput: UpdateOrgUnitInput & { companyCode?: 'KR' | 'CN' } = {
          name: formData.name || undefined,
          code: formData.code || undefined,
          order: formData.order ? parseInt(formData.order) : undefined,
          description: formData.description || undefined,
          approverUserId: formData.approverUserId || null,
          // V75.2: 조직 DL 이메일 (빈값/중복 제거)
          leaveNotificationDlEmails: Array.from(new Set(dlEmails.map(e => e.trim().toLowerCase()).filter(Boolean))),
        };

        // V26.1: type='company'인 경우에만 companyCode 포함
        if (formData.type === 'company' && formData.companyCode) {
          updateInput.companyCode = formData.companyCode as 'KR' | 'CN';
        }

        await updateOrgUnit(orgUnitId, updateInput);
        alert('조직이 수정되었습니다.');
      } else {
        // 생성 모드
        // V145: 조직장 필드 제거 / V145.1: 승인자 필드 복구
        // V26.1: type='company'일 때 companyCode 필수
        const createInput: CreateOrgUnitInput & { companyCode?: 'KR' | 'CN' } = {
          name: formData.name,
          code: formData.code || undefined,
          type: formData.type,
          parentId: formData.parentId || null,
          order: formData.order ? parseInt(formData.order) : undefined,
          isExternal: formData.isExternal,
          externalCompanyName: formData.externalCompanyName || undefined,
          description: formData.description || undefined,
          createdBy: '', // useOrgUnitMutations에서 자동 설정
          approverUserId: formData.approverUserId || null,
        };

        // V26.1: type='company'인 경우 companyCode 필수 포함
        if (formData.type === 'company' && formData.companyCode) {
          createInput.companyCode = formData.companyCode as 'KR' | 'CN';
        }

        await createOrgUnit(createInput);
        alert('조직이 생성되었습니다.');
      }

      // ✅ 성공 시: 폼 리셋 + 부모 컴포넌트 refetch
      setFormData(DEFAULT_FORM_DATA);
      setDlEmails([]);
      onSuccess(); // 부모에서 refetch() + 모달 닫기
    } catch (err: any) {
      console.error('조직 저장 실패:', err);
      // 사용자에게 에러 메시지 표시
      const errorMessage = err?.message || '조직 저장 중 오류가 발생했습니다. 관리자에게 문의하세요.';
      alert(errorMessage);
    }
  };

  // ============================================
  // 모달이 닫혀있으면 렌더링하지 않음
  // ============================================

  if (!isOpen) {
    return null;
  }

  // ============================================
  // 렌더링
  // ============================================

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()} // 내부 클릭 시 닫기 방지
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
            {isEditMode ? '조직 수정' : '새 조직 추가'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={24} style={{ color: 'var(--color-gray-600)' }} />
          </button>
        </div>

        {/* 로딩 (수정 모드에서 기존 데이터 로드 중) */}
        {isEditMode && isLoadingOrgUnit && (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Loader
              size={48}
              style={{
                color: 'var(--color-primary)',
                animation: 'spin 1s linear infinite',
                margin: '0 auto',
              }}
            />
            <p style={{ marginTop: '16px', color: 'var(--color-gray-600)' }}>
              조직 정보를 불러오는 중...
            </p>
          </div>
        )}

        {/* 폼 */}
        {(!isEditMode || !isLoadingOrgUnit) && (
          <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
            {/* 조직명 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                조직명 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="예: 플랫폼본부, 개발1실, A팀"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* 조직 코드 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                조직 코드
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                onBlur={(e) => checkCodeDuplication(e.target.value)}
                placeholder="예: HQ-PLATFORM, DIV-DEV1"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: codeError ? '1px solid var(--color-error)' : '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
              {codeError && (
                <p style={{ fontSize: '12px', color: 'var(--color-error)', marginTop: '4px' }}>
                  {codeError}
                </p>
              )}
            </div>

            {/* 조직 유형 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                조직 유형 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                disabled={isEditMode} // 수정 모드에서는 타입 변경 불가
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditMode ? 'var(--color-gray-100)' : 'white',
                }}
              >
                {ORG_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {isEditMode && (
                <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                  조직 유형은 수정할 수 없습니다.
                </p>
              )}
            </div>

            {/* V26.1: 회사 코드 (type='company'일 때만 표시) */}
            {formData.type === 'company' && (
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '6px',
                  }}
                >
                  회사 코드 <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <select
                  name="companyCode"
                  value={formData.companyCode}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: !formData.companyCode ? '1px solid var(--color-error)' : '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white',
                  }}
                >
                  <option value="">회사 코드를 선택하세요</option>
                  {COMPANY_CODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                  멀티회사 지원을 위한 회사 식별자입니다. 직원 생성/엑셀 업로드 시 회사 판정에 사용됩니다.
                </p>
                {!formData.companyCode && (
                  <p style={{ fontSize: '12px', color: 'var(--color-error)', marginTop: '4px' }}>
                    ⚠️ 회사 유형 조직은 회사 코드가 필수입니다.
                  </p>
                )}
              </div>
            )}

            {/* 상위 조직 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                상위 조직
              </label>
              <select
                name="parentId"
                value={formData.parentId}
                onChange={handleChange}
                disabled={isEditMode} // 수정 모드에서는 상위 조직 변경 불가
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditMode ? 'var(--color-gray-100)' : 'white',
                }}
              >
                <option value="">없음 (최상위 조직)</option>
                {activeOrgUnits.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.type})
                  </option>
                ))}
              </select>
              {isEditMode && (
                <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                  상위 조직은 수정할 수 없습니다. 조직 이동 기능을 사용하세요.
                </p>
              )}
            </div>

            {/* 정렬 순서 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                정렬 순서
              </label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleChange}
                placeholder="숫자가 작을수록 먼저 표시됩니다"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* V145.1: 승인자 선택 필드 복구 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                승인자 (선택 사항)
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={formData.approverName || (formData.approverUserId ? '이름 로딩 중...' : '')}
                  placeholder="선택되지 않음"
                  readOnly
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--color-gray-50)',
                    cursor: 'not-allowed',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setIsApproverPickerOpen(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    background: 'var(--color-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <UserPlus size={16} />
                  선택
                </button>
                {formData.approverUserId && (
                  <button
                    type="button"
                    onClick={handleApproverClear}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      background: 'var(--color-error, #EF4444)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <UserMinus size={16} />
                    해제
                  </button>
                )}
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                조직원의 각종 요청(휴가, 근로일정 등)을 승인하는 사람을 지정합니다.
              </p>
            </div>

            {/* 설명 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                설명
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="조직의 역할, 담당 업무 등을 간단히 설명해 주세요"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                }}
              />
            </div>

            {/* V75.2: 조직 DL 이메일 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px',
                }}
              >
                조직 DL (휴가 알림 메일)
              </label>
              <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '8px' }}>
                소속 직원의 휴가 최종승인 시 메일을 받을 DL 주소를 입력하세요. (@ngle.co.kr만 가능)
              </p>
              {dlEmails.map((email, idx) => {
                const trimmed = email.trim().toLowerCase();
                const hasError = trimmed !== '' && !trimmed.endsWith('@ngle.co.kr');
                return (
                  <div key={idx} style={{ marginBottom: '6px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          const next = [...dlEmails];
                          next[idx] = e.target.value;
                          setDlEmails(next);
                        }}
                        placeholder="example@ngle.co.kr"
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: `1px solid ${hasError ? 'var(--color-error, #ef4444)' : 'var(--color-gray-300)'}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setDlEmails(dlEmails.filter((_, i) => i !== idx))}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid var(--color-gray-300)',
                          borderRadius: '8px',
                          background: 'white',
                          color: 'var(--color-red-600)',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        삭제
                      </button>
                    </div>
                    {hasError && (
                      <p style={{ fontSize: '12px', color: 'var(--color-error, #ef4444)', marginTop: '2px', marginLeft: '4px' }}>
                        @ngle.co.kr 이메일만 입력할 수 있습니다
                      </p>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => setDlEmails([...dlEmails, ''])}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  background: 'var(--color-gray-50)',
                  color: 'var(--color-gray-700)',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                + 추가
              </button>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div
                style={{
                  marginBottom: '20px',
                  padding: '12px',
                  background: 'var(--color-error-light, #FEE2E2)',
                  border: '1px solid var(--color-error)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: 'var(--color-error)',
                }}
              >
                {error.message}
              </div>
            )}

            {/* 버튼 그룹 */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  color: 'var(--color-gray-700)',
                }}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !!codeError || (formData.type === 'company' && !formData.companyCode)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: isSubmitting || codeError || (formData.type === 'company' && !formData.companyCode) ? 'var(--color-gray-400)' : 'var(--color-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  cursor: isSubmitting || codeError || (formData.type === 'company' && !formData.companyCode) ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader
                      size={16}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                    저장 중...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {isEditMode ? '수정' : '생성'}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* V145.1: 승인자 선택 모달 복구 */}
      <EmployeePickerModal
        isOpen={isApproverPickerOpen}
        onClose={() => setIsApproverPickerOpen(false)}
        onSelect={handleApproverSelect}
        title="승인자 선택"
        selectedEmployeeId={formData.approverUserId}
      />
    </div>
  );
}

// Loader 애니메이션 스타일 추가
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);
